import { MapPin, Phone, Mail, Clock, ArrowRight, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { TikTokIcon } from "./icons/TikTokIcon";
import { COMPANY } from "../data/site";
import { CITIES, CITY_SLUGS } from "../data/cities";
import { useEstimate } from "../context/EstimateContext";

const EXTRA_AREAS = ["Catasauqua", "Coopersburg", "Alburtis", "Trexlertown"];

export const Contact = () => {
  const { openEstimate } = useEstimate();
  return (
    <section id="contact" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--apex-accent)]">Get in touch</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[var(--apex-primary)] mt-3 leading-tight">
            Let's talk about your project
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Free, no-pressure estimates throughout the Lehigh Valley. Send us the details or
            give us a call — we'll get right back to you.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="grid place-items-center h-11 w-11 rounded-sm bg-[var(--apex-surface)] text-[var(--apex-accent)] border border-slate-200"><Phone size={18} /></span>
              <div>
                <div className="text-xs text-slate-500">Call us</div>
                <div className="font-semibold text-[var(--apex-primary)] group-hover:text-[var(--apex-accent)] transition-colors">{COMPANY.phone}</div>
              </div>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="grid place-items-center h-11 w-11 rounded-sm bg-[var(--apex-surface)] text-[var(--apex-accent)] border border-slate-200"><Mail size={18} /></span>
              <div>
                <div className="text-xs text-slate-500">Email</div>
                <div className="font-semibold text-[var(--apex-primary)] group-hover:text-[var(--apex-accent)] transition-colors">{COMPANY.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-sm bg-[var(--apex-surface)] text-[var(--apex-accent)] border border-slate-200"><MapPin size={18} /></span>
              <div>
                <div className="text-xs text-slate-500">Shop</div>
                <div className="font-semibold text-[var(--apex-primary)]">{COMPANY.address}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-sm bg-[var(--apex-surface)] text-[var(--apex-accent)] border border-slate-200"><Clock size={18} /></span>
              <div>
                <div className="text-xs text-slate-500">Hours</div>
                <div className="font-semibold text-[var(--apex-primary)]">{COMPANY.hours}</div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Follow us</div>
            <div className="flex gap-3">
              <a
                href={COMPANY.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-facebook"
                aria-label="Apex Home Pro on Facebook"
                className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[var(--apex-primary)] hover:border-[var(--apex-accent)] hover:text-[var(--apex-accent)] transition-colors"
              >
                <Facebook size={18} /> Facebook
              </a>
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram"
                aria-label="Apex Home Pro on Instagram"
                className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[var(--apex-primary)] hover:border-[var(--apex-accent)] hover:text-[var(--apex-accent)] transition-colors"
              >
                <Instagram size={18} /> Instagram
              </a>
              <a
                href={COMPANY.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-tiktok"
                aria-label="Apex Home Pro on TikTok"
                className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[var(--apex-primary)] hover:border-[var(--apex-accent)] hover:text-[var(--apex-accent)] transition-colors"
              >
                <TikTokIcon size={18} /> TikTok
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-md overflow-hidden ring-1 ring-slate-200 shadow-lg h-[300px]">
            <iframe
              title="Apex Home Pro location"
              src="https://www.google.com/maps?q=801%20Broad%20St%2C%20Wescosville%2C%20PA&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
            />
          </div>

          <div className="mt-6 rounded-md bg-[var(--apex-primary)] p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-xl text-white">Ready for a free estimate?</h3>
              <p className="text-slate-300 text-sm mt-1">Tell us about your remodel and we'll follow up fast.</p>
            </div>
            <button
              data-testid="contact-estimate-btn"
              onClick={openEstimate}
              className="shrink-0 inline-flex items-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold px-6 py-3.5 transition-colors"
            >
              Get Free Estimate <ArrowRight size={18} />
            </button>
          </div>

          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Proudly serving</div>
            <div className="flex flex-wrap gap-2">
              {CITY_SLUGS.map((slug) => (
                <Link
                  key={slug}
                  to={`/remodeling/${slug}`}
                  data-testid={`area-link-${slug}`}
                  className="rounded-sm bg-white border border-slate-200 px-3 py-1 text-sm text-[var(--apex-primary)] font-medium hover:border-[var(--apex-accent)] hover:text-[var(--apex-accent)] transition-colors"
                >
                  {CITIES[slug].name}, PA
                </Link>
              ))}
              {EXTRA_AREAS.map((a) => (
                <span key={a} className="rounded-sm bg-[var(--apex-surface)] border border-slate-200 px-3 py-1 text-sm text-slate-600">{a}, PA</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
